<template>
    <div id="app" class="presentation-container d-flex flex-column">
        <!-- Background: video → named class → gradient -->
        <transition name="fade" mode="out-in">
            <!-- 1) Video if specified -->
            <video
                v-if="pathMeta.videoPath"
                key="video"
                class="bg-video"
                :src="pathMeta.videoPath"
                autoplay
                muted
                loop
                playsinline
            ></video>

            <!-- 2) CSS-class background if specified -->
            <div
                v-else-if="pathMeta.bgClass"
                :key="pathMeta.bgClass"
                :class="pathMeta.bgClass"
            ></div>

            <!-- 3) Default gradient -->
            <div v-else key="gradient" class="gradient-bg"></div>
        </transition>

        <!-- Vignette overlay -->
        <div
            class="vignette-bg"
            :class="pathMeta.vignetteGrade ? 'vignette-grade-' + pathMeta.vignetteGrade : ''"
        ></div>

        <!-- Content area -->
        <div class="content-area flex-grow-1 d-flex justify-content-center align-items-center">
            <transition name="fade-slide" mode="out-in">
                <router-view />
            </transition>
        </div>

        <!-- Navigation -->
        <SidebarNav v-if="navVisible" />
        <ManualSlideNav v-if="navVisible" />
    </div>
</template>

<script>
import SidebarNav from './components/SidebarNav.vue'
import ManualSlideNav from '@/components/ManualSlideNav.vue'
import { mapGetters } from 'vuex'

export default {
    name: 'App',
    components: { SidebarNav, ManualSlideNav },
    computed: {
        ...mapGetters(['allSlides']),
        navVisible() {
            return this.$route.path !== '/'
        },
        mainSlides() {
            return this.allSlides
        },
        pathMeta() {
            return this.$route.meta || {}
        }
    },
    methods: {
        onKeydown(e) {
            const slides = this.mainSlides
            let parentIndex = slides.findIndex(slide => {
                if (slide.path === this.$route.path) return true
                if (slide.children) {
                    return slide.children.some(c => c.path === this.$route.path)
                }
                return false
            })

            if (e.key === 'ArrowLeft') {
                e.preventDefault()
                const prev = (parentIndex - 1 + slides.length) % slides.length
                this.$router.push(slides[prev].path)
            } else if (e.key === 'ArrowRight') {
                e.preventDefault()
                const next = (parentIndex + 1) % slides.length
                this.$router.push(slides[next].path)
            } else if (e.key === 'ArrowDown') {
                const slide = slides[parentIndex]
                if (slide.children?.length) {
                    const children = slide.children
                    const idx = children.findIndex(c => c.path === this.$route.path)
                    if (idx < 0) {
                        this.$router.push(children[0].path)
                    } else if (idx < children.length - 1) {
                        this.$router.push(children[idx + 1].path)
                    }
                }
            } else if (e.key === 'ArrowUp') {
                const slide = slides[parentIndex]
                if (slide.children?.length) {
                    const children = slide.children
                    const idx = children.findIndex(c => c.path === this.$route.path)
                    if (idx > 0) {
                        this.$router.push(children[idx - 1].path)
                    } else if (idx === 0) {
                        this.$router.push(slide.path)
                    }
                }
            }
        }
    },
    mounted() {
        window.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeydown)
    }
}
</script>

<style scoped>
.presentation-container {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    padding-bottom: 3rem; /* avoid nav overlap */
}

/* 1) video background */
.bg-video {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -2;
}

/* 2) your existing gradient fallback */
.gradient-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #000013 0%, #42072c 100%);
    z-index: -2;
}

/* vignette overlay */
.vignette-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, transparent 60%, rgba(0,0,0,0.3) 100%);
    z-index: -1;
}

/* optional stronger grades */
.vignette-grade-1 {
    background: radial-gradient(circle at center, transparent 60%, rgba(0,0,0,0.3) 100%);
}
.vignette-grade-2 {
    background: radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.5) 100%);
}
.vignette-grade-3 {
    background: radial-gradient(circle at center, rgba(0,0,0,0.4) 20%,  rgba(0,0,0,0.8) 100%);
}

/* slide transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: opacity 0.5s ease, transform 0.5s ease;
}

.content-area {
    /* utility-centering */
}
</style>
